import { Box, Button, Flex, Grid, Icon, Text, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import { MdCheckCircle, MdOutlineCancel } from 'react-icons/md'

import useFollowUpAprovacao from '@hook/followUpFornecedor/use-aprovacao'
import useFilters from '@hook/shared/use-filters'
import useLayoutHeight from '@hook/shared/use-layout-height'

import HistoricoModal, {
  HistoricoState
} from '@component/FollowUpFornecedor/HistoricoModal'
import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'

import followUpFornecedorService from '@service/followUpFornecedor'

import {
  parseCSVLine,
  normalizeHeader
} from '../../../utils/followUpFornecedor/csv'
import AprovacaoModal from './AprovacaoModal'
import AprovacaoTable from './AprovacaoTable'
import UploadModal, { UploadRow } from './UploadModal'

const FollowUpFornecedorAprovacao: FC = () => {
  const height = useLayoutHeight()
  const { getFilters } = useFilters()

  const {
    loading,
    data,
    selected,
    params,
    currentyDate,
    modalAprovacao,
    getData,
    handleUpdateParams,
    handleToggleSelect,
    handleSelectAll,
    handleClearSelection,
    handleOpenModal,
    handleCloseModal,
    handleSubmitAprovacao,
    handleSingleAction,
    exporting,
    handleExport
  } = useFollowUpAprovacao()

  const [userOptions, setUserOptions] = useState<
    { value: number; label: string }[]
  >([])

  const toast = useToast()
  const [showFilters, setShowFilters] = useState(false)
  const [query, setQuery] = useState('')
  const [uploadModal, setUploadModal] = useState<{
    open: boolean
    rows: UploadRow[]
  }>({ open: false, rows: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [historico, setHistorico] = useState<HistoricoState>({
    open: false,
    loading: false,
    nomeProduto: '',
    documentId: null,
    rodadas: []
  })

  const handleOpenHistorico = async (item: {
    documentId: number
    productId: number
    nomeProduto: string
  }) => {
    setHistorico(h => ({
      ...h,
      open: true,
      loading: true,
      nomeProduto: item.nomeProduto,
      documentId: item.documentId,
      rodadas: []
    }))
    try {
      const { data: res } = await followUpFornecedorService.getHistoricoPar({
        documentId: item.documentId,
        productId: item.productId
      })
      setHistorico(h => ({ ...h, loading: false, rodadas: res.data ?? [] }))
    } catch {
      setHistorico(h => ({ ...h, loading: false }))
    }
  }

  useEffect(() => {
    // Backend aplica a regra por perfil (inclui Férias); vazio = combo escondido
    followUpFornecedorService
      .getUsuariosResponsaveisAprovacao()
      .then(({ data: res }) => {
        setUserOptions(
          (res.data ?? [])
            .filter((u: any) => u.id != null)
            .map((u: any) => ({
              value: Number(u.id),
              label: String(u.email)
            }))
        )
      })
      .catch(() => {})
    getData()
    getFilters({
      providersForPurchase: true,
      centers: true,
      productManagers: true,
      compactCategories: true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearAllFilters = () => {
    handleUpdateParams('fornecedores', [])
    handleUpdateParams('centros', [])
    handleUpdateParams('gerenteProduto', [])
    handleUpdateParams('categoriaCompacta', [])
    handleUpdateParams('statusAprovacao', [])
    handleUpdateParams('initialDate', '')
    handleUpdateParams('finalDate', '')
    handleUpdateParams('usuarios', [])
    getData()
  }

  const filtered = query
    ? data.filter(
        i =>
          i.nomeProduto.toLowerCase().includes(query.toLowerCase()) ||
          i.fornecedor.toLowerCase().includes(query.toLowerCase())
      )
    : data

  // Modelo usado pelo "Importar CSV" (exige as colunas ID e Aprovar)
  const handleExportModelo = () => {
    const header =
      'ID;Fornecedor;Centro;Cód. Produto;Produto;Doc.;A Fornecer;Nova Qtd.;Data Orig.;Nova Data;Observação;Estado;Status;Aprovar'
    const rows = filtered.map(i =>
      [
        i.id,
        `"${i.fornecedor}"`,
        `"${i.centro}"`,
        i.productId,
        `"${i.nomeProduto}"`,
        i.documentId,
        i.aFornecer,
        i.novaQuantidade ?? '',
        new Date(i.dataEstimada).toLocaleDateString('pt-BR'),
        i.novaDataEntrega
          ? (() => {
              const d = new Date(i.novaDataEntrega)
              return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR')
            })()
          : '',
        `"${i.observacao ?? ''}"`,
        i.estadoResposta,
        i.statusAprovacao === 1
          ? 'Aprovado'
          : i.statusAprovacao === 2
          ? 'Recusado'
          : 'Aguardando',
        ''
      ].join(';')
    )
    fileDownload(
      '\uFEFF' + [header, ...rows].join('\r\n'),
      'follow-up-aprovacao.csv',
      'text/csv;charset=utf-8'
    )
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = evt => {
      let text = (evt.target?.result as string) ?? ''
      text = text.replace(/^\uFEFF/, '')
      const lines = text.split(/\r?\n/).filter(l => l.trim())
      if (lines.length < 2) return
      const sep = lines[0].includes(';') ? ';' : ','
      const headers = parseCSVLine(lines[0], sep).map(normalizeHeader)
      const idIdx = headers.findIndex(h => h === 'id')
      const aprovarIdx = headers.findIndex(h => h === 'aprovar')
      if (idIdx === -1 || aprovarIdx === -1) {
        toast({
          title: 'CSV inválido: colunas ID e Aprovar são obrigatórias',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right'
        })
        return
      }
      const rows: UploadRow[] = []
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i], sep)
        const rawId = cols[idIdx]?.trim()
        const rawAprovar = cols[aprovarIdx]?.trim()
        if (!rawId || rawAprovar === '') continue
        const id = Number(rawId)
        const aprovarVal = Number(rawAprovar)
        if (isNaN(id) || (aprovarVal !== 0 && aprovarVal !== 1)) continue
        const item = data.find(d => d.id === id)
        if (!item || item.statusAprovacao !== 0) continue
        rows.push({
          id,
          statusAprovacao: aprovarVal === 1 ? 1 : 2,
          nomeProduto: item.nomeProduto,
          fornecedor: item.fornecedor
        })
      }
      if (rows.length === 0) {
        toast({
          title: 'Nenhuma alteração encontrada',
          description:
            'Verifique se os IDs correspondem a itens pendentes e se a coluna Aprovar tem valor 0 ou 1',
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'bottom-right'
        })
        return
      }
      setUploadModal({ open: true, rows })
    }
    reader.readAsText(file, 'UTF-8')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const confirmUpload = async () => {
    try {
      await followUpFornecedorService.submitAprovacao(
        uploadModal.rows.map(r => ({
          itemId: r.id,
          statusAprovacao: r.statusAprovacao,
          observacaoAprovador: null
        }))
      )
      toast({
        title: `${uploadModal.rows.length} item(s) processado(s) com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right'
      })
      setUploadModal({ open: false, rows: [] })
      await getData()
    } catch {
      toast({
        title: 'Erro ao processar aprovações',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right'
      })
    }
  }

  const filtersList = [
    {
      id: 'fornecedores',
      label: 'Fornecedor',
      filterId: 'providersForPurchase',
      type: 'select',
      isMulti: true,
      value: params.fornecedores
    },
    {
      id: 'centros',
      label: 'Centro',
      filterId: 'centers',
      type: 'select',
      isMulti: true,
      value: params.centros
    },
    {
      id: 'gerenteProduto',
      label: 'Gerente de Produto',
      filterId: 'productManagers',
      type: 'select',
      isMulti: true,
      value: params.gerenteProduto
    },
    {
      id: 'categoriaCompacta',
      label: 'Categoria Compacta',
      filterId: 'compactCategories',
      type: 'select',
      isMulti: true,
      value: params.categoriaCompacta
    },
    {
      id: 'period',
      label: 'Período do Disparo',
      type: 'date-input'
    },
    ...(userOptions.length > 0
      ? [
          {
            id: 'usuarios',
            label: 'Usuário Responsável',
            type: 'select-pagination',
            isMulti: true,
            options: userOptions
          }
        ]
      : [])
  ]

  const actions = useMemo(
    () => [
      {
        type: 'export' as const,
        exports: [
          {
            type: 'excel' as const,
            data: filtered,
            onClick: handleExport,
            loading: exporting
          }
        ]
      },
      {
        type: 'button' as const,
        label: 'Exportar Modelo p/ Importação',
        onClick: handleExportModelo
      },
      {
        type: 'button' as const,
        label: 'Importar CSV',
        onClick: () => fileInputRef.current?.click()
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, params, exporting]
  )

  const pendentes = filtered.filter(i => i.statusAprovacao === 0)
  const allPendentesSelected =
    pendentes.length > 0 &&
    pendentes.every(i => selected.some(s => s.id === i.id))

  return (
    <Layout title="Follow Up Fornecedor — Aprovação">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!showFilters ? '100vw' : '260px calc(100vw - 260px)'}
      >
        {showFilters && (
          <LayoutFilters
            filters={filtersList as any}
            values={params}
            defaultDate={currentyDate}
            onChange={({ name, value }) => handleUpdateParams(name, value)}
            loading={loading}
            canCancel={loading}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}

        <Box overflow="hidden" display="flex" flexDirection="column" h="100%">
          <LayoutTools
            title="Aprovação de Respostas"
            filters={showFilters}
            actions={actions}
            query={query}
            onSearchChange={setQuery}
            toggleFilters={() => setShowFilters(f => !f)}
            isDisabled={loading || data.length === 0}
          />

          {/* Bulk action bar */}
          {selected.length > 0 && (
            <Flex
              bg="#1d5840"
              color="white"
              px={5}
              py={3}
              align="center"
              gap={2}
              borderBottom="1px solid"
              borderColor="green.700"
            >
              <Text fontWeight="600" fontSize="14px" mr={2}>
                {selected.length}{' '}
                {selected.length === 1
                  ? 'item selecionado'
                  : 'itens selecionados'}
              </Text>
              {!allPendentesSelected && pendentes.length > 0 && (
                <Button
                  size="sm"
                  bg="whiteAlpha.100"
                  color="whiteAlpha.800"
                  _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
                  border="1px dashed"
                  borderColor="whiteAlpha.400"
                  onClick={handleSelectAll}
                >
                  Selecionar todos pendentes
                </Button>
              )}
              <Box w="1px" h="20px" bg="whiteAlpha.300" mx={2} />
              <Button
                size="sm"
                bg="green.500"
                color="white"
                leftIcon={<Icon as={MdCheckCircle} />}
                _hover={{ bg: 'green.400' }}
                onClick={() => handleOpenModal(1)}
              >
                Aprovar Selecionados
              </Button>
              <Button
                size="sm"
                bg="red.500"
                color="white"
                leftIcon={<Icon as={MdOutlineCancel} />}
                _hover={{ bg: 'red.400' }}
                onClick={() => handleOpenModal(2)}
              >
                Recusar Selecionados
              </Button>
              <Box flex={1} />
              <Button
                size="sm"
                variant="ghost"
                color="whiteAlpha.700"
                _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
                onClick={handleClearSelection}
              >
                Limpar seleção
              </Button>
            </Flex>
          )}

          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : filtered.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <Box flex={1} overflow="auto" px={5} py={4}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <AprovacaoTable
                filtered={filtered}
                selected={selected}
                pendentes={pendentes}
                allPendentesSelected={allPendentesSelected}
                onToggleSelect={handleToggleSelect}
                onSelectAll={handleSelectAll}
                onClearSelection={handleClearSelection}
                onSingleApprove={item => handleSingleAction(item, 1)}
                onSingleReject={item => handleSingleAction(item, 2)}
                onOpenHistorico={handleOpenHistorico}
              />
            </Box>
          )}
        </Box>
      </Grid>

      <HistoricoModal
        historico={historico}
        onClose={() => setHistorico(h => ({ ...h, open: false }))}
      />

      <UploadModal
        isOpen={uploadModal.open}
        rows={uploadModal.rows}
        onClose={() => setUploadModal({ open: false, rows: [] })}
        onConfirm={confirmUpload}
      />

      <AprovacaoModal
        isOpen={modalAprovacao.open}
        tipo={modalAprovacao.tipo as 1 | 2}
        count={selected.length}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAprovacao}
      />
    </Layout>
  )
}

export default FollowUpFornecedorAprovacao
